<script lang="ts" setup>
import { computed, ref, onMounted, watch } from 'vue'
import RoomieNavigationButton from '@lahaus-roomie/roomie/src/components/RoomieNavigationButton/index.vue'
import IconBack from '@lahaus-roomie/roomie/src/assets/icons/v2/back.svg'
import NextIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/next.svg'
import type { Props, DataTrack } from './types'
import InvestmentProjectCard from '@/components/InvestmentProjectCard/index.vue'
import { extractListingIdsFromProjectsSummaries } from '@/composables/useOnboarding/utils'
import './_index.scss'

const { t } = useI18n()
const { $useLhId } = useNuxtApp()

const props = withDefaults(defineProps<Props>(), {
  numRecommendations: 10,
  title: '',
  listingId: '',
  businessHubs: '',
  projects: () => [],
  screen: 'pdp',
  isBrochure: false
})

const title = computed(() => props.title || t('title'))

const slider = ref<HTMLElement | null>(null)
const sliders = ref<HTMLElement | null>(null)
const sliderScrollLeft = ref(0)
const scrollLeftLimit = ref(0)

const recommendations = computed(() => {
  if (!props.projects?.length) return []

  const filteredProjects = props.projects.filter((project: any) =>
    project.code !== props.listingId && project.business_hub === props.businessHubs
  )
  const limit = sliderScrollLeft.value > 0 ? props.numRecommendations : 4
  return filteredProjects.slice(0, limit)
})

const getCardWidth = (): number => {
  return sliders.value?.children[0]?.offsetWidth + 40
}

const scrollSlider = () => {
  slider.value?.scroll({
    behavior: 'smooth',
    left: sliderScrollLeft.value
  })
}

const moveLeft = () => {
  const newScrollLeft = sliderScrollLeft.value - getCardWidth() - 40
  sliderScrollLeft.value = Math.max(newScrollLeft, 0)
  scrollSlider()
}

const moveRight = () => {
  const cardWidth = getCardWidth()
  const newScrollLeft = sliderScrollLeft.value + cardWidth
  sliderScrollLeft.value = Math.min(newScrollLeft, scrollLeftLimit.value)
  scrollSlider()
}

const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement
  sliderScrollLeft.value = target.scrollLeft
}

const collectionId = ref<string>(`carousel-recommendations-pdp-${crypto.randomUUID()}`)

const trackCollectionData = () => {
  const listings = extractListingIdsFromProjectsSummaries(recommendations.value)
  const data: DataTrack = {
    collection_id: collectionId.value,
    description: 'PDPcarousel showing projects filtered by business hubs',
    collection_name: 'PDP by Business Hubs',
    screen: props.screen,
    listings,
    collection_criteria: [{
      field: 'Filter by recommendations of projects different from the current one but within the same business hubs',
      value: props.businessHubs,
      operator: 'equal'
    }]
  }
  trackListingCollectionViewed(data)
}

const trackListingCollectionViewed = (data: DataTrack): void => {
  const { $trackEvent } = useNuxtApp()
  $trackEvent({
    name: 'listing_collection_viewed',
    version: '1-0-0',
    data
  })
}

const calculateScrollLeftLimit = (scrollLeftLimit: any, slider: any) => {
  return slider.value ? slider.value.scrollWidth - slider.value.clientWidth : scrollLeftLimit.value
}

onMounted(() => {
  scrollLeftLimit.value = calculateScrollLeftLimit(scrollLeftLimit, slider)

  trackCollectionData()
})

watch(sliderScrollLeft, () => {
  scrollLeftLimit.value = calculateScrollLeftLimit(scrollLeftLimit, slider)
})

const COOKIE_EXPIRATION = 60 * 10

const handleCardClick = (cardIndex: number, collectionId: string | undefined, projectCode: string | undefined): void => {
  const cookieName = `listingCollection_last_card_${projectCode}`
  const collectionCookie = useCookie(cookieName, { maxAge: COOKIE_EXPIRATION })

  collectionCookie.value = {
    collection_id: collectionId,
    index: cardIndex
  }
}
</script>

<script lang="ts">
export default {
  name: 'RecommendationsCarousel'
}
</script>

<template>
  <section>
    <div class="recommendations-carousel mt-48 lg:mt-56">
      <h2
        v-if="!isBrochure"
        class="title display-6 lg:display-5 text-pine-600 pb-32 lg:pb-48 px-16">
        {{ title }}
      </h2>

      <div class="recommendations-carousel__holder flex items-center justify-center">
        <RoomieNavigationButton
          v-if="recommendations.length >= 3 && sliderScrollLeft !== 0"
          id="recommendations-carousel-slider-left"
          size="sm"
          variant="outlined"
          class="recommendations-carousel__slider-button relative z-10"
          :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'desplazar', C: 'atras'})"
          aria-label="Atrás"
          @click="moveLeft">
          <IconBack />
        </RoomieNavigationButton>

        <div
          id="recommendations-carousel__slider"
          ref="slider"
          class="overflow-x-auto pl-16 lg:pl-24 lg:pl-0 scrollbar-hidden"
          :class="{ 'w-screen px-0': isBrochure }"
          :data-lh-id="`${screen}-recommendations-slider`"
          @scroll="handleScroll">
          <div
            ref="sliders"
            class="flex gap-12 lg:gap-16">
            <InvestmentProjectCard
              v-for="(project, index) in recommendations"
              :key="project.code"
              :project="project"
              :site="screen"
              :tracking-page="dataAttributes.dataLh.P"
              :tracking-zone="dataAttributes.dataLh.Z"
              :tracking-index="String(index)"
              :is-brochure="isBrochure"
              clickable
              @card-click="handleCardClick(index, collectionId, project.code )" />

            <span class="block md:hidden w-1 flex-none" />
          </div>
        </div>

        <RoomieNavigationButton
          v-if="recommendations.length >= 3 && sliderScrollLeft !== scrollLeftLimit"
          id="recommendations-carousel-slider-right"
          size="sm"
          variant="outlined"
          class="recommendations-carousel__slider-button relative z-10"
          :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'desplazar', C: 'siguiente'})"
          aria-label="Siguiente"
          @click="moveRight">
          <NextIcon />
        </RoomieNavigationButton>
      </div>
    </div>
  </section>
</template>

<i18n src="./i18n.json" lang="json"></i18n>
